import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../services/api';
import '../../styles/dashboard-pages.css';

function AlertsUpdates() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const getNotificationIcon = (notification) => {
    const icons = {
      security_score_changed: 'monitoring',
      external_scan_changed: 'security',
      vendor_risk_changed: 'account_tree',
      verification_changed: 'verified',
      company_user_joined: 'person_add',
      company_access_changed: 'admin_panel_settings',
    };
    return icons[notification?.type] || 'notifications';
  };

  const formatNotificationTime = (notification) => {
    const raw = notification?.created_at;
    if (!raw) return '';

    if (typeof raw === 'string' && /^\d{1,2}:\d{2}$/.test(raw)) {
      return raw;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return String(raw);
    }

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed);
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await getNotifications({ unreadOnly: selectedCategory === 'unread' });
        setNotifications(
          selectedCategory === 'critical'
            ? data.filter((notification) => notification.severity === 'critical')
            : data
        );
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchNotifications();
  }, [selectedCategory]);

  const openNotification = async (notification) => {
    if (!notification.read_at) {
      await markNotificationRead(notification.id);
      setNotifications((current) => current.map((item) => (
        item.id === notification.id ? { ...item, read_at: new Date().toISOString() } : item
      )));
    }
    if (notification.action_url) navigate(notification.action_url);
  };

  return (
    <div className="alerts-page">
      <div className="page-header">
        <div>
          <h2>Notifications</h2>
          <p>Review important security, verification, vendor, and account changes.</p>
        </div>
        {notifications.some((notification) => !notification.read_at) && (
          <button
            type="button"
            className="notifications-read-all"
            onClick={async () => {
              await markAllNotificationsRead();
              setNotifications((current) => current.map((item) => ({
                ...item,
                read_at: item.read_at || new Date().toISOString(),
              })));
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="filter-section">
        <button 
          className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          All
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'unread' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('unread')}
        >
          <span className="material-symbols-outlined">mark_email_unread</span>
          Unread
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'critical' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('critical')}
        >
          <span className="material-symbols-outlined">warning</span>
          Critical
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <div className="notifications-empty">
          <span className="material-symbols-outlined">notifications_none</span>
          <p>No notifications in this view.</p>
        </div>
      ) : (
        <div className="activity-log">
          {notifications.map((notification) => (
            <button
              type="button"
              key={notification.id}
              className={`log-entry notification-entry ${notification.severity} ${notification.read_at ? '' : 'is-unread'}`}
              onClick={() => openNotification(notification)}
            >
              <div className="log-time">{formatNotificationTime(notification)}</div>
              <div className="log-content">
                <div className="log-icon">
                  <span className="material-symbols-outlined">{getNotificationIcon(notification)}</span>
                </div>
                <div className="notification-copy">
                  <div className="notification-title-row">
                    <strong>{notification.title}</strong>
                    {!notification.read_at && <span className="notification-unread-dot" />}
                  </div>
                  <p className="log-message">{notification.message}</p>
                </div>
                {notification.action_url && (
                  <span className="material-symbols-outlined notification-chevron">chevron_right</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AlertsUpdates;
